/**
 * /community — The Playbook Boards (SAA community forum).
 *
 *  Landing renders the 4 boards + latest thread stub.
 *  Click a board → renders its threads (pinned first, then recent).
 *  Compose a new thread with rich body — LLM moderation runs server-side
 *  before publish; rejections surface as a red toast with the reason.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Plus, Pin, Lock, MessageSquare, ThumbsUp, Zap,
  Trophy, BookOpen, Sparkles, Users, Eye, X, ShieldCheck, ShieldAlert, Paperclip,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAttachments, AttachmentPreview } from '../lib/attachments';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const ICON_BY_SLUG = {
  'the-court': Trophy,
  'academic-playbook': BookOpen,
  'nil-personal-brand': Sparkles,
  'parent-locker-room': Users,
};

// ─── Landing (all boards) ────────────────────────────────────────────────────
export const ForumHub = () => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/academy/forum/boards', { headers: authHeaders() })
      .then((r) => setBoards(r.data.boards || []))
      .catch(() => toast.error('Could not load forum'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="forum-hub">
      <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.08), transparent)' }}>
        <div className="container mx-auto max-w-5xl">
          <Link to="/academy" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Academy
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">The Playbook Boards</div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            SAA <span className="text-orange-500">Community</span>
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
            Four boards. One code: honorific vocabulary, zero cursing, respect on every post. All submissions are moderated by the Coach B AI before publish.
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-6 py-10 space-y-3">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> :
          boards.map((b) => {
            const Icon = ICON_BY_SLUG[b.slug] || MessageSquare;
            return (
              <button
                key={b.slug}
                onClick={() => navigate(`/community/${b.slug}`)}
                className="w-full border-2 border-zinc-800 bg-zinc-950 hover:border-orange-500 transition-all p-5 flex items-center gap-5 text-left"
                data-testid={`board-${b.slug}`}
                style={{ borderLeftColor: b.accent, borderLeftWidth: 4 }}
              >
                <div className="w-12 h-12 border-2 flex-shrink-0 flex items-center justify-center" style={{ borderColor: `${b.accent}66` }}>
                  <Icon className="w-5 h-5" style={{ color: b.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg md:text-xl font-black text-white leading-tight">{b.name}</div>
                  <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{b.description}</p>
                  {b.latest_thread && (
                    <p className="text-[11px] text-zinc-600 mt-2 truncate">Latest: <span className="text-zinc-400">"{b.latest_thread.title}"</span> by {b.latest_thread.author_name}</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-3xl font-black text-white leading-none">{b.thread_count || 0}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">Threads</div>
                </div>
              </button>
            );
          })
        }
      </div>
    </div>
  );
};

// ─── Board view (thread list + new thread composer) ─────────────────────────
export const ForumBoard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent'); // recent | top
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        api.get('/academy/forum/boards', { headers: authHeaders() }),
        api.get(`/academy/forum/boards/${slug}/threads?sort=${sort}`, { headers: authHeaders() }),
      ]);
      setBoard((b.data.boards || []).find((x) => x.slug === slug));
      setThreads(t.data.threads || []);
    } catch { toast.error('Could not load board'); }
    finally { setLoading(false); }
  }, [slug, sort]);

  useEffect(() => { load(); }, [load]);

  const Icon = ICON_BY_SLUG[slug] || MessageSquare;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="forum-board">
      <div className="border-b-2 border-zinc-900 py-8 px-6">
        <div className="container mx-auto max-w-5xl">
          <Link to="/community" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> All Boards
          </Link>
          <div className="flex items-center gap-3">
            <Icon className="w-6 h-6" style={{ color: board?.accent || '#FF5722' }} />
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight">{board?.name || slug}</h1>
          </div>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">{board?.description}</p>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setSort('recent')} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 ${sort === 'recent' ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`} data-testid="sort-recent">Recent</button>
            <button onClick={() => setSort('top')} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 ${sort === 'top' ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`} data-testid="sort-top">Top</button>
          </div>
          <button onClick={() => setComposing(true)} className="bg-orange-500 hover:bg-orange-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5" data-testid="new-thread-btn">
            <Plus className="w-3.5 h-3.5" /> New Thread
          </button>
        </div>

        {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> :
          threads.length === 0 ? <p className="text-center text-zinc-600 py-16 text-sm">No threads yet. Break the silence.</p> :
            <div className="space-y-2">
              {threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => navigate(`/community/thread/${t.id}`)}
                  className="w-full border-2 border-zinc-800 bg-zinc-950 hover:border-orange-500 transition-all p-4 flex items-start gap-4 text-left"
                  data-testid={`thread-row-${t.id}`}
                >
                  <div className="w-10 h-10 flex-shrink-0 border-2 border-zinc-800 flex items-center justify-center text-[10px] font-black text-zinc-400">
                    {(t.author_name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.is_pinned && <Pin className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
                      {t.is_locked && <Lock className="w-3.5 h-3.5 text-zinc-500" />}
                      <h3 className="font-black text-white text-base md:text-lg truncate">{t.title}</h3>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      by <b className="text-zinc-300">{t.author_name}</b> · {(t.created_at || '').replace('T', ' ').slice(0, 16)} UTC
                    </p>
                    <p className="text-sm text-zinc-400 mt-2 line-clamp-2 leading-relaxed">{t.body}</p>
                  </div>
                  <div className="flex-shrink-0 grid grid-cols-3 gap-3 text-center">
                    <Stat icon={Eye} value={t.view_count || 0} label="Views" />
                    <Stat icon={MessageSquare} value={t.reply_count || 0} label="Replies" />
                    <Stat icon={ThumbsUp} value={(t.upvotes || []).length} label="Upvotes" />
                  </div>
                </button>
              ))}
            </div>
        }
      </div>

      {composing && <NewThreadModal boardSlug={slug} onClose={() => setComposing(false)} onCreated={() => { setComposing(false); load(); }} />}
    </div>
  );
};

const Stat = ({ icon: Icon, value, label }) => (
  <div>
    <Icon className="w-3.5 h-3.5 text-zinc-600 mx-auto" />
    <div className="text-sm font-black text-white mt-0.5">{value}</div>
    <div className="text-[8px] uppercase tracking-widest text-zinc-600">{label}</div>
  </div>
);

// ─── Thread reader + reply composer ──────────────────────────────────────────
export const ForumThread = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const replyAtt = useAttachments();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, meR] = await Promise.all([
        api.get(`/academy/forum/threads/${id}`, { headers: authHeaders() }),
        api.get('/academy/staff/directory', { headers: authHeaders() }).catch(() => ({ data: { staff: [] } })),
      ]);
      setThread(t.data.thread);
      setReplies(t.data.replies || []);
      // Derive "me" from directory match (staff) — otherwise minimal identity
      const token = localStorage.getItem('academy_token');
      try {
        const meResp = await api.get('/academy/auth/me', { headers: authHeaders() });
        setMe(meResp.data.user || null);
      } catch { setMe(null); }
    } catch { toast.error('Could not load thread'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = me?.is_admin || me?.role === 'teacher' || me?.role === 'founder';

  const submitReply = async () => {
    if (!reply.trim() && replyAtt.attachments.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post(`/academy/forum/threads/${id}/replies`, {
        body: reply || '(attachment)',
        parent_reply_id: replyTo?.id || null,
        attachments: replyAtt.attachments,
      }, { headers: authHeaders() });
      setReplies((prev) => [...prev, r.data.reply]);
      setReply(''); setReplyTo(null); replyAtt.reset();
      toast.success('Reply posted');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Reply failed';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const react = async (kind, targetId, isReply = false) => {
    try {
      const path = isReply
        ? `/academy/forum/replies/${targetId}/${kind}`
        : `/academy/forum/threads/${targetId}/${kind}`;
      const r = await api.post(path, {}, { headers: authHeaders() });
      // Refresh locally
      if (isReply) {
        setReplies((prev) => prev.map((x) => x.id === targetId ? { ...x, [kind]: Array(r.data.count).fill('') } : x));
      } else {
        setThread((prev) => ({ ...prev, [kind]: Array(r.data.count).fill('') }));
      }
    } catch { toast.error('Could not react'); }
  };

  const adminAction = async (action) => {
    if (action === 'delete' && !window.confirm('Delete this thread?')) return;
    try {
      await api.post(`/academy/forum/admin/threads/${id}`, { action }, { headers: authHeaders() });
      toast.success(`Thread ${action}ed`);
      if (action === 'delete') { navigate(`/community/${thread.board_slug}`); return; }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  if (loading || !thread) return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  // Group replies by root
  const rootReplies = replies.filter((r) => !r.parent_reply_id);
  const nested = replies.filter((r) => r.parent_reply_id);
  const childrenOf = (rid) => nested.filter((n) => n.parent_reply_id === rid);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-32" data-testid="forum-thread">
      <div className="border-b-2 border-zinc-900 py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to={`/community/${thread.board_slug}`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Back to Board
          </Link>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {thread.is_pinned && <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 px-2 py-1"><Pin className="w-2.5 h-2.5" /> Pinned</span>}
            {thread.is_locked && <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-zinc-400 border border-zinc-700 px-2 py-1"><Lock className="w-2.5 h-2.5" /> Locked</span>}
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">{thread.title}</h1>
          <div className="mt-4 flex items-center gap-3 text-[11px] text-zinc-500">
            <span>by <b className="text-zinc-300">{thread.author_name}</b></span>
            <span>·</span>
            <span>{(thread.created_at || '').replace('T', ' ').slice(0, 16)} UTC</span>
            <span>·</span>
            <span>{thread.view_count || 0} views</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-8 space-y-6">
        {/* Body */}
        <div className="border-2 border-zinc-800 bg-zinc-950 p-6">
          <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{thread.body}</p>
          {(thread.attachments || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {thread.attachments.map((a, i) => <AttachmentPreview key={i} att={a} />)}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center gap-3">
            <ReactionBtn count={(thread.upvotes || []).length} onClick={() => react('upvote', thread.id)} icon={ThumbsUp} label="Upvote" testid="thread-upvote" />
            <ReactionBtn count={(thread.applause || []).length} onClick={() => react('applause', thread.id)} icon={Zap} label="Applause" testid="thread-applause" />
            {isAdmin && (
              <div className="ml-auto flex items-center gap-1">
                <AdminBtn onClick={() => adminAction(thread.is_pinned ? 'unpin' : 'pin')} icon={Pin} label={thread.is_pinned ? 'Unpin' : 'Pin'} testid="admin-pin" />
                <AdminBtn onClick={() => adminAction(thread.is_locked ? 'unlock' : 'lock')} icon={Lock} label={thread.is_locked ? 'Unlock' : 'Lock'} testid="admin-lock" />
                <AdminBtn onClick={() => adminAction('delete')} icon={X} label="Delete" testid="admin-delete" danger />
              </div>
            )}
          </div>
        </div>

        {/* Replies */}
        <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mt-8 mb-3">
          {rootReplies.length} {rootReplies.length === 1 ? 'Reply' : 'Replies'}
        </div>
        {rootReplies.map((r) => (
          <div key={r.id} data-testid={`reply-${r.id}`} className="border-2 border-zinc-800 bg-zinc-950 p-4">
            <ReplyHeader reply={r} />
            <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap mt-2">{r.body}</p>
            {(r.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {r.attachments.map((a, i) => <AttachmentPreview key={i} att={a} />)}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <ReactionBtn count={(r.upvotes || []).length} onClick={() => react('upvote', r.id, true)} icon={ThumbsUp} label="Upvote" testid={`reply-upvote-${r.id}`} small />
              <ReactionBtn count={(r.applause || []).length} onClick={() => react('applause', r.id, true)} icon={Zap} label="Applause" testid={`reply-applause-${r.id}`} small />
              {!thread.is_locked && (
                <button onClick={() => setReplyTo(r)} className="text-[9px] font-black uppercase tracking-widest text-zinc-500 hover:text-white ml-2" data-testid={`reply-to-${r.id}`}>Reply</button>
              )}
            </div>
            {/* Nested */}
            {childrenOf(r.id).map((child) => (
              <div key={child.id} className="ml-6 mt-3 border-l-2 border-zinc-800 pl-4 pb-1" data-testid={`nested-${child.id}`}>
                <ReplyHeader reply={child} />
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap mt-2">{child.body}</p>
                <div className="mt-2 flex items-center gap-2">
                  <ReactionBtn count={(child.upvotes || []).length} onClick={() => react('upvote', child.id, true)} icon={ThumbsUp} testid={`nested-upvote-${child.id}`} small />
                  <ReactionBtn count={(child.applause || []).length} onClick={() => react('applause', child.id, true)} icon={Zap} testid={`nested-applause-${child.id}`} small />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Composer */}
      {!thread.is_locked && (
        <div className="fixed bottom-0 left-0 right-0 border-t-2 border-zinc-900 bg-[#0B0E14]/95 backdrop-blur-sm p-4 z-30" data-testid="reply-composer">
          <div className="container mx-auto max-w-4xl">
            {replyTo && (
              <div className="flex items-center justify-between mb-2 text-[10px] text-zinc-500">
                <span>Replying to <b className="text-zinc-300">{replyTo.author_name}</b>: "{replyTo.body.slice(0, 60)}…"</span>
                <button onClick={() => setReplyTo(null)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {replyAtt.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="reply-attachments">
                {replyAtt.attachments.map((a, i) => (
                  <AttachmentPreview key={i} att={a} onRemove={() => replyAtt.removeAt(i)} />
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply(); } }}
                placeholder="Reply with honor. All posts are moderated by the Coach B AI…"
                className="flex-1 bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none resize-none"
                rows={2}
                data-testid="reply-input"
              />
              <label className="border-2 border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500 px-3 py-2.5 cursor-pointer" data-testid="reply-attach" title="Attach">
                {replyAtt.uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                <input type="file" accept="image/*,video/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) replyAtt.addFile(f); e.target.value = ''; }} />
              </label>
              <button onClick={submitReply} disabled={busy || replyAtt.uploading || (!reply.trim() && replyAtt.attachments.length === 0)} className="bg-orange-500 hover:bg-orange-400 text-black px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-1.5 disabled:opacity-40" data-testid="reply-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Post
              </button>
            </div>
          </div>
        </div>
      )}
      {thread.is_locked && (
        <div className="fixed bottom-0 left-0 right-0 border-t-2 border-zinc-900 bg-[#0B0E14]/95 backdrop-blur-sm p-4 z-30 text-center text-[11px] font-black uppercase tracking-widest text-zinc-500" data-testid="locked-notice">
          <Lock className="inline w-3.5 h-3.5 mr-1.5" /> Thread Locked — Read-Only
        </div>
      )}
    </div>
  );
};

const ReplyHeader = ({ reply }) => (
  <div className="flex items-center gap-2 text-[11px] text-zinc-500">
    <div className="w-6 h-6 border border-zinc-800 flex items-center justify-center text-[9px] font-black text-zinc-400">
      {(reply.author_name || '?').slice(0, 2).toUpperCase()}
    </div>
    <b className="text-zinc-200">{reply.author_name}</b>
    <span>·</span>
    <span>{(reply.created_at || '').replace('T', ' ').slice(0, 16)} UTC</span>
  </div>
);

const ReactionBtn = ({ icon: Icon, count, onClick, label, testid, small }) => (
  <button onClick={onClick} className={`flex items-center gap-1 ${small ? 'text-[10px] px-2 py-1' : 'text-[11px] px-2.5 py-1.5'} border border-zinc-800 hover:border-orange-500 text-zinc-400 hover:text-orange-400`} data-testid={testid}>
    <Icon className={small ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> {count}{label && !small ? ` ${label}` : ''}
  </button>
);

const AdminBtn = ({ icon: Icon, label, onClick, testid, danger }) => (
  <button onClick={onClick} className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1.5 border-2 ${danger ? 'border-red-500/40 text-red-400 hover:bg-red-500/10' : 'border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-400'}`} data-testid={testid}>
    <Icon className="w-3 h-3" /> {label}
  </button>
);

const NewThreadModal = ({ boardSlug, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const { attachments, uploading, addFile, removeAt, reset } = useAttachments();
  const save = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and body required'); return; }
    setBusy(true);
    try {
      await api.post('/academy/forum/threads', {
        board_slug: boardSlug, title, body, attachments,
      }, { headers: authHeaders() });
      toast.success('Thread posted — welcome to the boards');
      reset();
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Post failed';
      if (msg.toLowerCase().includes('moderation')) {
        toast.error(msg, { duration: 8000, icon: '⚠️' });
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="new-thread-modal">
      <div className="w-full max-w-2xl bg-zinc-950 border-2 border-zinc-800 p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">New Thread</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Thread title" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none" data-testid="new-thread-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Say something worth reading. Honorific vocabulary. Zero cursing. The Coach B AI is watching." rows={6} className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none resize-none" data-testid="new-thread-body" />

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="new-thread-attachments">
            {attachments.map((a, i) => (
              <AttachmentPreview key={i} att={a} onRemove={() => removeAt(i)} />
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 mb-4">
          <label className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-orange-500 border-2 border-zinc-800 hover:border-orange-500 px-3 py-2 cursor-pointer transition-colors" data-testid="new-thread-attach">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
            {uploading ? 'Uploading…' : 'Attach Image / Video'}
            <input type="file" accept="image/*,video/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) addFile(f); e.target.value = ''; }} />
          </label>
          <div className="flex items-center gap-1 text-[10px] text-zinc-500">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
            <span>AI-moderated for the SAA Code of Excellence before publish.</span>
          </div>
        </div>
        <button onClick={save} disabled={busy || uploading} className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="new-thread-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Publish Thread
        </button>
      </div>
    </div>
  );
};

export default ForumHub;
